import {Box, createStyles, Text} from '@mantine/core';
import React from 'react';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import MiniPlateSummary from 'notebook/integrations/plate-maps/scratch-plates/MiniPlateSummary';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {Header2} from 'ui-library/Headers';

import {MAX_PREVIEW_HEIGHT, MAX_PREVIEW_WIDTH} from './PopoverPreviewStyles';

// Horizontal breathing room reserved beside the plate grid so it doesn't run edge-to-edge in the
// popover; the target well size is derived from the remaining width.
const PLATE_HORIZONTAL_MARGIN_PX = 100;
const MAX_PLATE_WIDTH_PX = MAX_PREVIEW_WIDTH - PLATE_HORIZONTAL_MARGIN_PX;

// Wells render at a fixed physical size so plate formats stay visually comparable: a 384-well plate
// (24 columns) fills the popover width less a fixed margin, which pins each well at this size, and
// smaller plates reuse it (e.g. a 96-well plate — half the columns and rows — renders at a quarter
// the area). Plates larger than the popover box on either axis (e.g. 1536-well, or tall/narrow
// plates) are uniformly scaled down to fit, so their wells render smaller than this target.
const TARGET_WELL_PX = MAX_PLATE_WIDTH_PX / 24;

function isValidDimension(value: number): boolean {
  return Number.isInteger(value) && value >= 1;
}

/**
 * SVG width (`sizePx`) for the plate grid: the grid at its target well size, uniformly scaled down
 * only when it would overflow the popover's width or height bounds. MiniPlateSummary derives the
 * rendered height from this width and the plate's aspect ratio, so bounding the width by
 * `MAX_PREVIEW_HEIGHT * columns / rows` keeps the rendered height within `MAX_PREVIEW_HEIGHT`.
 */
function getSummaryWidthPx(columns: number, rows: number): number {
  const naturalWidth = columns * TARGET_WELL_PX;
  const widthCappedByHeight = (MAX_PREVIEW_HEIGHT * columns) / rows;
  return Math.min(naturalWidth, MAX_PLATE_WIDTH_PX, widthCappedByHeight);
}

/** Human-readable plate format, e.g. "96-well plate (8 × 12)". */
function getPlateShapeLabel(width: number, height: number): string {
  return `${width * height}-well plate (${height} × ${width})`;
}

/**
 * Popover contents for a scratch plate: the plate's name and format above a grid sized to its
 * dimensions, with filled wells shaded from the plate's server-computed summary. Plates without a
 * summary yet render an empty grid. The header spacing mirrors the dataset popover's header; the
 * name and shape, at larger plate sizes, help quickly tell plate-producing step outputs apart.
 */
export default function PlatePopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.PLATE>;
}): JSX.Element | null {
  const {classes} = useStyles();
  const {name, width, height} = rawData;
  if (!isValidDimension(width) || !isValidDimension(height)) {
    return null;
  }

  return (
    <Box>
      <div className={classes.header}>
        {name != null && name !== '' && <Header2>{name}</Header2>}
        <Text color="dimmed" size="sm">
          {getPlateShapeLabel(width, height)}
        </Text>
      </div>
      <Box p="md">
        <MiniPlateSummary
          width={width}
          height={height}
          filledPositions={rawData.summary?.wellStates ?? []}
          sizePx={getSummaryWidthPx(width, height)}
        />
      </Box>
    </Box>
  );
}

// Match the dataset popover's Pane header spacing: 12px top/right/left, 8px bottom, solid divider.
const useStyles = createStyles((theme) => ({
  header: {
    padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm}`,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
}));
